import { IEnvironment } from './environment.interface';

/**
 * Build-time env release of the runtime-toggleable feature flags (the `FEATURE_FLAG_CATALOG`
 * subset), per environment. Single source of truth spread into both the client env files and the
 * feature-flags admin app, so a flag's release is defined exactly once. Keep the keys in sync with
 * `FEATURE_FLAG_CATALOG`.
 *
 * Plain object-literal constants on purpose: the client spreads these into `environment.featureSwitches`,
 * which Angular's AOT compiler must statically evaluate (it is read in `@NgModule` metadata). A
 * `satisfies` / computed-key map would not be statically analyzable and would force JIT.
 */
type FeatureFlagRelease = Pick<
  IEnvironment['featureSwitches'],
  | 'akteAttachmentsMail'
  | 'datalossWarningInDatenerfassung'
  | 'deactivateDoctor'
  | 'heilmittelverordnung'
  | 'locations'
  | 'mailClientDienstkennungen'
  | 'mailversandTerminerinnerung'
  | 'muster12'
  | 'muster12Blanko'
  | 'muster13Blanko'
  | 'muster26'
  | 'muster26Blanko'
  | 'muster27'
  | 'muster27Blanko'
  | 'muster7Blanko'
  | 'terminbestaetigung'
  | 'terminkalenderV3'
  | 'eigeneRegelnAuswertung'
  | 'eigeneRegelnAbrechnung'
  | 'tiKonfiguration'
>;

export const FEATURE_FLAG_RELEASE_DEVELOPMENT: FeatureFlagRelease = {
  akteAttachmentsMail: true,
  datalossWarningInDatenerfassung: false,
  deactivateDoctor: true,
  heilmittelverordnung: true,
  locations: true,
  mailClientDienstkennungen: false,
  mailversandTerminerinnerung: true,
  muster12: true,
  muster12Blanko: true,
  muster13Blanko: true,
  muster26: true,
  muster26Blanko: true,
  muster27: true,
  muster27Blanko: true,
  muster7Blanko: true,
  terminbestaetigung: true,
  terminkalenderV3: true,
  eigeneRegelnAuswertung: true,
  eigeneRegelnAbrechnung: true,
  tiKonfiguration: true,
};

export const FEATURE_FLAG_RELEASE_E2E: FeatureFlagRelease = {
  akteAttachmentsMail: true,
  datalossWarningInDatenerfassung: false,
  deactivateDoctor: true,
  heilmittelverordnung: false,
  locations: true,
  mailClientDienstkennungen: false,
  mailversandTerminerinnerung: false,
  muster12: true,
  muster12Blanko: false,
  muster13Blanko: false,
  muster26: true,
  muster26Blanko: false,
  muster27: true,
  muster27Blanko: false,
  muster7Blanko: true,
  terminbestaetigung: true,
  terminkalenderV3: false,
  eigeneRegelnAuswertung: true,
  eigeneRegelnAbrechnung: true,
  tiKonfiguration: true,
};

export const FEATURE_FLAG_RELEASE_INTEGRATION: FeatureFlagRelease = {
  akteAttachmentsMail: true,
  datalossWarningInDatenerfassung: true,
  deactivateDoctor: true,
  heilmittelverordnung: false,
  locations: true,
  mailClientDienstkennungen: false,
  mailversandTerminerinnerung: false,
  muster12: true,
  muster12Blanko: false,
  muster13Blanko: false,
  muster26: true,
  muster26Blanko: false,
  muster27: true,
  muster27Blanko: false,
  muster7Blanko: false,
  terminbestaetigung: true,
  terminkalenderV3: false,
  eigeneRegelnAuswertung: true,
  eigeneRegelnAbrechnung: true,
  tiKonfiguration: true,
};

export const FEATURE_FLAG_RELEASE_STAGING: FeatureFlagRelease = {
  akteAttachmentsMail: true,
  datalossWarningInDatenerfassung: true,
  deactivateDoctor: true,
  heilmittelverordnung: false,
  locations: true,
  mailClientDienstkennungen: false,
  mailversandTerminerinnerung: false,
  muster12: true,
  muster12Blanko: false,
  muster13Blanko: false,
  muster26: true,
  muster26Blanko: false,
  muster27: true,
  muster27Blanko: false,
  muster7Blanko: false,
  terminbestaetigung: true,
  terminkalenderV3: false,
  eigeneRegelnAuswertung: true,
  eigeneRegelnAbrechnung: true,
  tiKonfiguration: true,
};

export const FEATURE_FLAG_RELEASE_TESTSERVER: FeatureFlagRelease = {
  akteAttachmentsMail: true,
  datalossWarningInDatenerfassung: true,
  deactivateDoctor: true,
  heilmittelverordnung: false,
  locations: true,
  mailClientDienstkennungen: false,
  mailversandTerminerinnerung: false,
  muster12: true,
  muster12Blanko: false,
  muster13Blanko: false,
  muster26: true,
  muster26Blanko: false,
  muster27: true,
  muster27Blanko: false,
  muster7Blanko: false,
  terminbestaetigung: true,
  terminkalenderV3: false,
  eigeneRegelnAuswertung: true,
  eigeneRegelnAbrechnung: true,
  tiKonfiguration: true,
};

export const FEATURE_FLAG_RELEASE_PRODUCTION: FeatureFlagRelease = {
  akteAttachmentsMail: true,
  datalossWarningInDatenerfassung: true,
  deactivateDoctor: true,
  heilmittelverordnung: false,
  locations: true,
  mailClientDienstkennungen: false,
  mailversandTerminerinnerung: false,
  muster12: true,
  muster12Blanko: false,
  muster13Blanko: false,
  muster26: true,
  muster26Blanko: false,
  muster27: true,
  muster27Blanko: false,
  muster7Blanko: false,
  terminbestaetigung: true,
  terminkalenderV3: false,
  eigeneRegelnAuswertung: true,
  eigeneRegelnAbrechnung: true,
  tiKonfiguration: false,
};
